<template>
  <div id="app">
    <TermsAcceptedConsent v-if="showTerms && isManager && authStore.user_id" @hide="showTerms = false" />
    <HelpDrawer :visible="showHelp" @hide="showHelp = false" />
    <UpdateDrawer :visible="showUpdates" @hide="showUpdates = false" />
    <IESupport v-if="isIE" />
    <router-view />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useRoute } from "vue-router";
import { EventBus } from "./eventbus";
import { useAuthStore } from "@/stores/auth";
import { useUsersStore } from "@/stores/users";
import { useAccountStore } from "@/stores/account";
import { useAccountSettingsStore } from "@/stores/accountSettings";
import { usePermissionStore } from "@/stores/permission";
import { useVersionCheck } from "@/composables/useVersionCheck";

import TermsAcceptedConsent from "@/TermsAcceptedConsent.vue";
import IESupport from "@/IESupport.vue";
import HelpDrawer from "@/components/Drawer/HelpDrawer.vue";
import UpdateDrawer from "@/components/Drawer/UpdateDrawer.vue";

// Stores
const authStore = useAuthStore();
const usersStore = useUsersStore();
const accountStore = useAccountStore();
const accountSettingsStore = useAccountSettingsStore();
const permissionStore = usePermissionStore();

// Route
const route = useRoute();

// Poll for a newer deployed build and offer a reload when one is found.
useVersionCheck();

// Reactive state
const isIE = ref(false);
const showTerms = ref(false);
const showHelp = ref(false);
const showUpdates = ref(false);
const tokenLoading = ref(false);

// Computed
const isManager = computed(() => permissionStore.roles.includes("manager"));

// Methods
function checkIfIsIE() {
  const ua = navigator.userAgent;
  isIE.value = ua.indexOf("MSIE ") > -1 || ua.indexOf("Trident/") > -1;
}

async function checkTCAccepted() {
  if (authStore.user_id) {
    try {
      const account = await accountStore.getCurrentAccount();
      if (!account.data.terms_accepted) {
        showTerms.value = true;
      }
    } catch {
      // Silently handle errors
    }
  }
}

function handleLoadingToken(state: unknown) {
  if (route.path !== "/") {
    tokenLoading.value = state as boolean;
  }
}

function setHelpVisibility(state: unknown) {
  showHelp.value = state as boolean;
}

function setUpdatesVisibility(state: unknown) {
  showUpdates.value = state as boolean;
}

// Watch route changes
watch(
  () => route.path,
  () => {
    checkTCAccepted();
  },
);

// Lifecycle
onMounted(async () => {
  checkIfIsIE();

  // Set up event listeners
  EventBus.$on("loading-token", handleLoadingToken);
  EventBus.$on("helpVisible", setHelpVisibility);
  EventBus.$on("updateVisible", setUpdatesVisibility);

  checkTCAccepted();
});

// Load the session data once the acting user is known: right away after a reload, after the
// account choice on the first visit from the Keycloak login. A later switch between accounts
// reloads it itself (setCurrentAccount → reloadSessionData).
watch(
  () => authStore.user_id,
  (userId, previous) => {
    if (userId && !previous) {
      usersStore.getUserList();
      accountSettingsStore.getAccountSettings();
      checkTCAccepted();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  EventBus.$off("helpVisible", setHelpVisibility);
  EventBus.$off("updateVisible", setUpdatesVisibility);
  EventBus.$off("loading-token", handleLoadingToken);
});
</script>
